/**
 * air_choreo.ts — the war-in-the-sky choreography (PURE — no three, no DOM;
 * node-testable, the SpaceModule war doctrine: every pose is a function of
 * (battle seed, side, index, wall-clock t), so every client sees the same
 * ballet with zero networking).
 *
 * Geometry is BATTLE-RELATIVE: x/z offsets from the battle's target point,
 * y above the ground at the battle center. The module adds the center + ground.
 *
 *   cruiserPose   each side's capital ships on slow counter-rotating orbits
 *                 above the field (side a clockwise, side b counter; a second
 *                 or third cruiser rides a third of a turn behind, higher).
 *   fighterPose   a BANKED RACETRACK (war tweaks 2026-09-17): each fighter
 *                 flies its own rotated ellipse at ~85 m/s with a BREAK turn
 *                 every 18–26 s (the turn rate doubles for four seconds), an
 *                 altitude wave, and scheduled STRAFING RUNS (dive to ~12 m
 *                 over an aim point near the enemy line, pull up) — `strafe`
 *                 rises 0→1 through the run, bottoming at 0.5. Both sides
 *                 share the volume and fly opposite ways: a dogfight, not two
 *                 parades. Research: air combat manoeuvring (lead / pure / lag
 *                 pursuit, energy), coordinated turns (bank from turn rate).
 *   strafeAim     where a fighter's run is aimed (jittered toward the enemy
 *                 side's ground formation).
 *   departPose    the climb out of the field once the BATTLE is over — the
 *                 wing leaves into space, never pops out (war tweaks).
 *   airWingSize   how many fighters and cruisers a side fields, from the town's
 *                 chest and the civ's space store (war tweaks).
 *   hullBank      the bank angle a hull shows for a turn rate at a speed.
 */

export interface AirPose {
  x: number; z: number;      // battle-relative
  y: number;                 // above the battle-center ground
  /** 0 = cruising the band; else 0..1 progress through the current strafe run. */
  strafe: number;
  /** Monotone id of the current/last strafe run (fires ground FX once per run). */
  runIdx: number;
}

const TAU = Math.PI * 2;

/** FNV-1a with the murmur finalizer (the HASH LAW). */
export function airH(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export const CRUISER_ALT = 250;      // m above the field
export const CRUISER_ORBIT_R = 430;  // m
export const FIGHTERS_PER_SIDE = 3;     // the base draft (war_air_ops AIR_FIGHTERS_PER_SIDE); the economy adds up to AIR_FIGHTERS_MAX
export const STRAFE_LEN_S = 4.6;     // one dive, bottom at k = 0.5
/** The racetrack: cruise speed and the break-turn cadence (war tweaks 2026-09-17). */
export const FIGHTER_SPEED_MPS = 85;
export const BREAK_LEN_S = 4;
export const BREAK_GAIN = 0.6;        // the turn rate rises ×1.6 at the break's peak
/** Bank: atan(v·ω / g), clamped — a coordinated turn's angle at this speed and yaw rate. */
export const BANK_MAX_RAD = Math.PI / 3;
export function hullBank(speedMps: number, yawRateRadPerS: number): number {
  const b = Math.atan((speedMps * yawRateRadPerS) / 9.81);
  return Math.max(-BANK_MAX_RAD, Math.min(BANK_MAX_RAD, b));
}

/** The capital ships' orbits — counter-rotating per side, altitude split; `idx` (0..2) spaces a side's cruisers a third of a turn apart and 30 m higher each. */
export function cruiserPose(seed: string, side: 'a' | 'b', t: number, idx = 0): AirPose {
  const s = side === 'a' ? 0 : 1;
  const dir = s === 0 ? 1 : -1;
  const th = airH(`${seed}|cap${s}`) * TAU + dir * t * 0.026 + idx * (TAU / 3);
  return {
    x: Math.cos(th) * CRUISER_ORBIT_R,
    z: Math.sin(th) * CRUISER_ORBIT_R,
    y: CRUISER_ALT + s * 46 + idx * 30 + Math.sin(t * 0.11 + s * 2 + idx) * 9,
    strafe: 0, runIdx: 0,
  };
}

/** When fighter (side, i) is mid-run, and how far through it. */
export function strafeWindow(seed: string, side: 'a' | 'b', i: number, t: number): { k: number; runIdx: number } {
  const h = airH(`${seed}|${side}|sw${i}`);
  const period = 36 + h * 20;                      // each fighter has its own cadence — a dive every 36–56 s (the server wounds on every dive; 3/min was a meat grinder)
  const local = t + h * period;                    // de-phased so runs interleave
  const runIdx = Math.floor(local / period);
  const into = local - runIdx * period;
  return { k: into < STRAFE_LEN_S ? into / STRAFE_LEN_S : 0, runIdx };
}

/** Where a run is aimed: a jittered point near the ENEMY side's ground line
 *  (formations wing ±z of the field; the jitter re-rolls every run). */
export function strafeAim(seed: string, side: 'a' | 'b', i: number, runIdx: number): { x: number; z: number } {
  const enemyWing = side === 'a' ? 1 : -1; // mirror of battle_math's wing split
  const jx = (airH(`${seed}|${side}|ax${i}|${runIdx}`) - 0.5) * 26;
  const jz = (airH(`${seed}|${side}|az${i}|${runIdx}`) - 0.5) * 14;
  return { x: jx, z: enemyWing * (7 + airH(`${seed}|${side}|aw${i}|${runIdx}`) * 9) + jz };
}

/** The racetrack's phase at t: the base angular rate plus every BREAK's extra advance (integrated, so the rate — not the position — jumps). */
function trackPhase(t: number, w0: number, period: number, phase0: number): number {
  const local = t + phase0 * period;
  const cyc = Math.floor(local / period);
  const into = local - cyc * period;
  const perBreak = (BREAK_LEN_S * 2) / Math.PI;                       // ∫ sin(πx/L) dx over a break = 2L/π
  const partial = into < BREAK_LEN_S ? (BREAK_LEN_S / Math.PI) * (1 - Math.cos((Math.PI * into) / BREAK_LEN_S)) : perBreak;
  return w0 * (local + BREAK_GAIN * (cyc * perBreak + partial));
}

/** A deployed fighter: the banked racetrack, interrupted by strafing dives. */
export function fighterPose(seed: string, side: 'a' | 'b', i: number, t: number): AirPose {
  const s = side === 'a' ? 0 : 1;
  const h1 = airH(`${seed}|${side}|f${i}a`);
  const h2 = airH(`${seed}|${side}|f${i}b`);
  const h3 = airH(`${seed}|${side}|f${i}c`);
  // The track: a rotated ellipse each fighter owns; side a flies it clockwise, side b counter — they meet head-on over the field.
  const A = 210 + h1 * 50, B = A * (0.8 + h2 * 0.1);
  const rot = h3 * TAU;
  const perim = TAU * Math.sqrt((A * A + B * B) / 2);
  const w0 = (FIGHTER_SPEED_MPS / perim) * TAU;
  const period = 18 + h2 * 8;                                          // a break every 18–26 s
  const dir = s === 0 ? 1 : -1;
  const ph = trackPhase(t, w0, period, h1) * dir;
  const ex = Math.cos(ph) * A, ez = Math.sin(ph) * B;
  const cx = (h2 - 0.5) * 120, cz = (h1 - 0.5) * 120;                  // the track's centre sits off the target
  const bx = cx + ex * Math.cos(rot) - ez * Math.sin(rot);
  const bz = cz + ex * Math.sin(rot) + ez * Math.cos(rot);
  const by = 120 + Math.sin(t * 0.13 + h2 * TAU) * 34 + s * 18;
  const { k, runIdx } = strafeWindow(seed, side, i, t);
  if (k <= 0) return { x: bx, z: bz, y: by, strafe: 0, runIdx };
  // The dive: blend track → aim with sin(kπ) so the run bottoms at k=0.5
  // (~12 m AGL over the aim point) and pulls back up symmetrically.
  const aim = strafeAim(seed, side, i, runIdx);
  const blend = Math.sin(k * Math.PI);
  return {
    x: bx * (1 - blend) + aim.x * blend,
    z: bz * (1 - blend) + aim.z * blend,
    y: by * (1 - blend) + 12 * blend,
    strafe: k, runIdx,
  };
}

/* ── War-persistent states (2026-08-29 war rework): the ships belong to the
 *    WAR (war_air_unit rows), not the battle — these poses cover the states
 *    the server assigns between and after ground battles. All pure. ── */

/** Chaser trails the evader's own path, delayed — a readable tail chase (lag pursuit). */
export const PURSUIT_DELAY_S = 1.15;
/** Every other dogfight epoch the chaser cuts inside the evader's path (lead pursuit). */
export const PURSUIT_LEAD_S = 0.7;
/** The pursuit delay this epoch: lag on even epochs, lead on odd. */
export function pursuitDelay(t: number): number {
  return Math.floor(t / DOGFIGHT_EPOCH_S) % 2 === 0 ? PURSUIT_DELAY_S : PURSUIT_LEAD_S;
}

/** Escort racetrack: a stretched loop swept fast — reads as covering a march
 *  (used when the escorted craft's live pose is not available). */
export function escortPose(seed: string, side: 'a' | 'b', i: number, t: number): AirPose {
  const s = side === 'a' ? 0 : 1;
  const h = airH(`${seed}|${side}|esc${i}`);
  const dir = s === 0 ? 1 : -1;
  const th = h * TAU + dir * t * (0.085 + h * 0.03);
  const stretch = h * TAU; // loop orientation
  const ex = Math.cos(th) * 540, ez = Math.sin(th) * 210;
  return {
    x: ex * Math.cos(stretch) - ez * Math.sin(stretch),
    z: ex * Math.sin(stretch) + ez * Math.cos(stretch),
    y: 150 + Math.sin(t * 0.19 + h * TAU) * 24 + i * 9,
    strafe: 0, runIdx: 0,
  };
}

/**
 * THE ARRIVAL LAW (2026-09-20): a craft that leaves is flying SOMEWHERE, and it
 * flies the WHOLE way there — climb-out, cruise, a 3° approach, touchdown — and
 * is removed only once it has ARRIVED. No hull is ever hidden for crossing a
 * radius or an altitude, or reaped on a flat timer while it is still airborne:
 * "I never want a versona ship to vanish, it needs to be realistic."
 *
 * A pure function of TIME and the real distance home, so every client draws the
 * same hull in the same place without streaming a position (the choreography law).
 */
export const CLIMB_TAN = 0.1405;        // tan 8° — the climb-out gradient (matches flight_profile's CLIMB_DEG)
export const GLIDE_TAN = 0.0524;        // tan 3° — the international glideslope (flight_profile's GLIDESLOPE_DEG)

/** Ground speed of a homebound hull (m/s). */
export function homeSpeed(cruiser: boolean): number { return cruiser ? 42 : 88; }
/** Its height over the field when the order comes, and the height it cruises home at. */
export function homeStartAlt(cruiser: boolean): number { return cruiser ? 260 : 140; }
export function homeCruiseAlt(cruiser: boolean): number { return cruiser ? 380 : 260; }

/** Seconds for a homebound hull to cover `distM` — what the reaper waits for. */
export function homeFlightS(distM: number, cruiser: boolean): number {
  return Math.max(0, (Math.max(0, distM) - 60) / homeSpeed(cruiser));
}

/**
 * The vertical profile over `d` metres of a `distM` leg: climb at 8° to a cruise
 * height, hold, then descend at 3° to touch down exactly at the town. A leg too
 * short for both tops out LOWER rather than flying steeper (the short-leg law of
 * flight_profile); one too short even to descend from where it starts glides
 * straight down to the town.
 */
export function homeAlt(d: number, distM: number, cruiser: boolean): number {
  const y0 = homeStartAlt(cruiser), span = Math.max(1, distM);
  const dd = Math.min(Math.max(0, d), span);
  const peak = Math.min(homeCruiseAlt(cruiser), (span + y0 / CLIMB_TAN) / (1 / CLIMB_TAN + 1 / GLIDE_TAN));
  if (peak <= y0) return y0 * (1 - dd / span);           // no room to climb: a straight glide home
  const climbRun = (peak - y0) / CLIMB_TAN, descRun = peak / GLIDE_TAN;
  if (dd < climbRun) return y0 + dd * CLIMB_TAN;
  if (dd > span - descRun) return Math.max(0, (span - dd) * GLIDE_TAN);
  return peak;
}

/** The flight home once the war resolves: from the anchor to the wing's own town,
 *  the whole distance, landing on arrival. `distM` known → it arrives; unknown →
 *  the legacy climb-out (only when the town cannot be resolved at all). */
export function homePose(seed: string, airKey: string, sinceS: number, cruiser: boolean, toward?: number, distM?: number): { x: number; z: number; y: number; heading: number; arrived: boolean } {
  const heading = toward ?? airH(`${seed}|home|${airKey}`) * TAU;
  const span = distM !== undefined && distM > 0 ? distM : Infinity;
  const d = Math.min(60 + Math.max(0, sinceS) * homeSpeed(cruiser), span);
  return {
    x: Math.cos(heading) * d,
    z: Math.sin(heading) * d,
    y: Number.isFinite(span)
      ? homeAlt(d, span, cruiser)
      : (cruiser ? 260 : 140) + Math.min(400, Math.max(0, sinceS) * 14),   // no town to fly to: the legacy climb-out
    heading,
    arrived: Number.isFinite(span) && d >= span,
  };
}

/** The climb out once the BATTLE resolves (war tweaks 2026-09-17): the wing
 *  leaves the field INTO SPACE — a beeline that keeps climbing (fighters
 *  16 m/s, cruisers 9 m/s; the client hides a hull past DEPART_HIDE_ALT_M and
 *  the server reaps the row after AIR_DEPART_TTL_S). */
export const DEPART_HIDE_ALT_M = 2500;
/** A hull bound for a KNOWN home (war tweaks 2026-09-18) is hidden past this distance from its anchor — it flew home, it did not climb to orbit. */
export const DEPART_HIDE_DIST_M = 5500;
/** `toward` (anchor-relative bearing, radians) and `distM` are the pilot's HOME: a departure
 *  flies the FULL way to the wing's town and lands there (the ARRIVAL LAW above). Without a
 *  resolvable town it falls back to the legacy climb-out. */
export function departPose(seed: string, airKey: string, sinceS: number, cruiser: boolean, toward?: number, distM?: number): { x: number; z: number; y: number; heading: number; arrived: boolean } {
  const heading = toward ?? airH(`${seed}|depart|${airKey}`) * TAU;
  const span = distM !== undefined && distM > 0 ? distM : Infinity;
  const since = Math.max(0, sinceS);
  const d = Math.min(60 + since * homeSpeed(cruiser), span);
  return {
    x: Math.cos(heading) * d,
    z: Math.sin(heading) * d,
    y: Number.isFinite(span)
      ? homeAlt(d, span, cruiser)
      : (cruiser ? 260 : 140) + since * (cruiser ? 12 : 16),   // no town to fly to: the legacy climb-out
    heading,
    arrived: Number.isFinite(span) && d >= span,
  };
}

/** How many fighters and cruisers a side fields (war tweaks 2026-09-17): the
 *  base wing plus the town's chest and the civ's space store — a rich side
 *  darkens the sky. Ceilings mirror the catalog's AIR_FIGHTERS_MAX / AIR_CRUISERS_MAX. */
export function airWingSize(treasury: bigint, civHulls: number, civCapitals: number): { fighters: number; cruisers: number } {
  const t = Number(treasury < 0n ? 0n : treasury);
  const fighters = Math.max(3, Math.min(7, 3 + Math.floor(t / 500) + Math.floor(Math.max(0, civHulls) / 15)));
  const cruisers = Math.max(1, Math.min(3, 1 + Math.floor(Math.max(0, civCapitals) / 3) + (t >= 1500 ? 1 : 0)));
  return { fighters, cruisers };
}

/** Dogfight epochs (mirrors the server's 90 s exchange cadence): within an
 *  epoch each cross-side pair holds one chaser and one evader, swapping next
 *  epoch — the fight reads as turns of advantage, not two parades. */
export const DOGFIGHT_EPOCH_S = 90;

/** True when side `side`'s fighter is the CHASER of its pair this epoch. */
export function dogfightChaser(seed: string, side: 'a' | 'b', pair: number, t: number): boolean {
  const epoch = Math.floor(t / DOGFIGHT_EPOCH_S);
  const h = airH(`${seed}|duel|${pair}|${epoch}`);
  return (h < 0.5) === (side === 'a');
}
